import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useSetAtom } from 'jotai';
import React, { useCallback, useState } from 'react';

import { Box, Button, Collapse, EntryList, Flex, Icon } from '~/components/blocks';
import { deleteFileModalState } from '~/state/partials/delete_file_modal/atoms';
import { isPdf } from '~/utils/file';
import { openWithNoOpener } from '~/utils/window';

import { CollapseLabel } from './CollapseLabel';
import { ImgViewer } from './ImgViewer';
import { PdfViewer } from './PdfViewer';

export type AttachmentFile = {
  id: string;
  date: string;
  name: string;
  contentType: string;
  url: string;
  received: boolean;
};

type Props = {
  file: AttachmentFile;
  patientName?: string;
};

const PreviewButton = styled(Button)(({ theme }) =>
  css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    width: '40px',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radii.circle,
    opacity: 0,
    transform: 'translate(-50%,-50%)',
    transition: `opacity linier ${theme.transitions.fast}`,
  }),
);

const Content = styled(Box)(({ theme, onClick }) =>
  css({
    marginTop: theme.space.m,
    position: 'relative',
    ...(onClick && {
      background: theme.colors.background.bg,
      cursor: 'pointer',
      '&:hover': {
        background: theme.colors.background.grey,
        opacity: 1,
        [`& > ${PreviewButton}`]: {
          opacity: 1,
        },
      },
    }),
  }),
);

export const File = React.memo((props: Props) => {
  const { file, patientName } = props;

  const theme = useTheme();

  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [hasPdfError, setHasPdfError] = useState(false);

  const setDeleteFileModalState = useSetAtom(deleteFileModalState);
  const handleOpenDeleteModal = useCallback(
    () =>
      setDeleteFileModalState({
        isOpen: true,
        fileId: file.id,
        fileName: file.name,
      }),
    [file.id, file.name, setDeleteFileModalState],
  );
  const handleClickImage = useCallback(() => {
    openWithNoOpener(file.url, {
      target: file.name,
    });
  }, [file.name, file.url]);
  const handlePdfError = useCallback(() => setHasPdfError(true), []);
  const handlePdfLoad = useCallback(() => setIsPdfLoading(false), []);

  return (
    <Collapse label={<CollapseLabel received={file.received} date={file.date} name={file.name} />}>
      <Box marginY={theme.space.xs}>
        <EntryList>
          <EntryList.Body>
            <Content
              onClick={
                isPdf(file.contentType) && (hasPdfError || isPdfLoading)
                  ? undefined
                  : handleClickImage
              }
            >
              {isPdf(file.contentType) ? (
                <PdfViewer src={file.url} onError={handlePdfError} onLoad={handlePdfLoad} />
              ) : (
                <ImgViewer src={file.url} alt={file.name} />
              )}
              <PreviewButton icon use="base">
                <Icon color="white" icon="blank" size="l" />
              </PreviewButton>
            </Content>
          </EntryList.Body>
        </EntryList>
        <Flex justifyContent="space-between" alignItems="flex-end">
          <EntryList marginTop={theme.space.m}>
            <EntryList.Head>ファイル名</EntryList.Head>
            <EntryList.Body>
              <strong>{file.name}</strong>
            </EntryList.Body>
          </EntryList>
          {!file.received && (
            <Box textAlign="right">
              <Button icon align="right" use="white" size="s" onClick={handleOpenDeleteModal}>
                <Icon size="m" icon="trash" />
                ファイル削除
              </Button>
            </Box>
          )}
        </Flex>
        {file.received && patientName && (
          <EntryList marginTop={theme.space.m} marginBottom={theme.space.m}>
            <EntryList.Head>送信者</EntryList.Head>
            <EntryList.Body>{patientName}</EntryList.Body>
          </EntryList>
        )}
      </Box>
    </Collapse>
  );
});

File.displayName = 'File';
